import { Component, OnInit, ViewEncapsulation, OnChanges, SimpleChanges } from '@angular/core';

import { map, mergeMap, takeUntil } from 'rxjs/operators';

import { VraagboomEmptyResultaat } from '@models/vragenboom';
import { Vraag, Antwoord, Vraagtype, Formaat, BmSimulatie } from '@models/claims';
import { FormatHelper, SharedDataService, VraagHelper } from '@services/public_api';
import { BaseVraagControlDirective } from '../base/base-vraag-control';
import { MetenMapper } from './../../../mappers/meten.mapper';
import { Proces } from './../../../models/generic/proces.enum';
import { DataService } from './../../../services/data.service';

@Component({
  selector: 'app-label',
  templateUrl: './label.component.html',
  styleUrls: ['./label.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class LabelComponent extends BaseVraagControlDirective<VraagboomEmptyResultaat> implements OnInit, OnChanges {
  isSchadedetail: boolean;
  formaat = Formaat;
  vraagtype = Vraagtype;
  tekstenBmGevolgen: string[] = [];
  tekstenBmBelangrijk: string[] = [];
  bmSimulaties: BmSimulatie[];
  geschatSchadeBedrag: number;
  eigenRisicoBedragAanwezig: boolean;
  eigenRisicoBedrag: number;
  eigenRisicoBedragFormat: string;
  bmgevolgenDisclaimerTonen = false;

  constructor(
    _dataService: DataService,
    private _sharedDataService: SharedDataService
  ) {
    super(_dataService);
  }

  ngOnInit() {
    setTimeout(() => {
      this.setAntwoord(this.vraag.antwoorden, new VraagboomEmptyResultaat());
    });

    this.isSchadedetail = (this._dataService.getProces() === Proces.Schadedetail);

    if (this.vraag.vraagtype === Vraagtype.LabelBmGevolgen || this.vraag.vraagtype === Vraagtype.LabelBmGrijs ||
      this.vraag.connectedVragen.find(v => v.vraagtype === Vraagtype.LabelBmGrijs) ||
      this.vraag.connectedVragen.find(v => v.vraagtype === Vraagtype.LabelBmGevolgen)) {
      this.bepaalGegevensLijsten();
      this.bepaalBonusMalusTeksten();
    }
  }

  initialize(changes: SimpleChanges): void { }

  stylingClass(vraag: Vraag, connectedvraag: boolean): string {
    if (!vraag || !vraag.vraagtype) {
      return '';
    }
    // als de (volgende) vraag een label-break is dan geen margin-bottom
    const volgendeVraag = this.geefVolgendeVraag(vraag, connectedvraag);
    if (!volgendeVraag || vraag.vraagtype === this.vraagtype.LabelBreak || volgendeVraag.vraagtype === this.vraagtype.LabelBreak) {
      return 'ols-no-mb ' + vraag.vraagtype.toLowerCase();
    }
    // als de volgende vraag een andere groep-id heeft dan wel margin-bottom
    if (vraag.groep_id === volgendeVraag.groep_id) {
      if (vraag.vraagtype === Vraagtype.Label8 && volgendeVraag.vraagtype === Vraagtype.Label8) {
        return vraag.vraagtype.toLowerCase();
      }
      return 'ols-mb ' + vraag.vraagtype.toLowerCase();
    }
    return vraag.vraagtype.toLowerCase();
  }

  geefVolgendeVraag(vraag: Vraag, connectedvraag: boolean): Vraag {
    // volgende vraag binnen de groep, deze zit in connectedVragen
    if (connectedvraag) {
      return this.vraag.connectedVragen.find((cv: Vraag) => cv.id === vraag.linkt_naar);
    } else {
      if (this.vraag.connectedVragen?.length > 0) {
        return vraag.connectedVragen[0];
      }
    }
    return undefined;
  }

  geefUrl(regels: string[]): string {
    if (regels == null || regels.length === 0) {
      return '';
    }
    return regels[0];
  }

  geefEuro(antwoord: Antwoord, str: string): string {
    let formaat = this.geefFormaat(antwoord, str);
    if (formaat === Formaat.Bedrag || formaat === Formaat.BedragNegatief || formaat === Formaat.BedragTotaal) {
      return '\u20AC';
    }
    return '';
  }

  geefRegelVraag(vraag: Vraag, regel: string, metEuroteken: boolean): string {
    let formaat = undefined;
    if (vraag?.antwoorden?.length > 0) {
      formaat = vraag?.antwoorden[0].formaat;
    }
    return this.geefRegel(regel, metEuroteken, formaat);
  }

  geefJuisteBMSimulatie(): BmSimulatie {
    let bmSimulatie = this.bmSimulaties.find(bm => bm.berekendJaar === 5);
    if (!bmSimulatie) {
      // Alleen als 5 jaar niet gevonden wordt
      bmSimulatie = this.bmSimulaties.find(bm => bm.bmtredeWelClaimen >= (this.bmSimulaties[0].bmtredeNietClaimen - 1));
    }
    return bmSimulatie;
  }

  geefBmTekstPremietoeslag(): string {
    let tekst = 'Je betaalt geen extra premie. ';
    if (this.eigenRisicoBedragAanwezig && this.eigenRisicoBedrag > 0) {
      return tekst + 'Je betaalt wel ' + this.eigenRisicoBedragFormat +
        ' eigen risico. Als de reparatiekosten hoger zijn dan ' + this.eigenRisicoBedragFormat +
        ' kun je de reparatie <span class="ols-bold">het beste door ons</span> laten betalen. Dit mag je natuurlijk zelf bepalen.';
    } else {
      return tekst + 'Je kunt de reparatie <span class="ols-bold">het beste door ons</span> laten betalen. Dit mag je natuurlijk zelf bepalen.';
    }
  }

  geefBmTekstGeenMeerpremie(): string {
    return 'Je betaalt over 5 jaar geen extra premie. Je kunt de schade misschien <span class="ols-bold">het beste door ons</span> laten betalen.';
  }

  geefBmTekstBedragOnbekend() {
    let bmSimulatie = this.geefJuisteBMSimulatie();

    if (bmSimulatie?.premiegevolgenTotaal && bmSimulatie.premiegevolgenTotaal > 0) {
      let tekst = 'Je betaalt over ' + bmSimulatie.berekendJaar + ' jaar ongeveer ' + this.formatBedrag(bmSimulatie.premiegevolgenTotaal) + ' extra premie.';
      if (this.eigenRisicoBedragAanwezig) {
        tekst += ' Ook betaal je ' + this.eigenRisicoBedragFormat + ' eigen risico. Dit is totaal ' + this.formatBedrag(bmSimulatie.premiegevolgenTotaal + this.eigenRisicoBedrag) + '.';
      }
      this.tekstenBmGevolgen.push(tekst);
      let bedrag = this.eigenRisicoBedragAanwezig ? this.formatBedrag(bmSimulatie.premiegevolgenTotaal + this.eigenRisicoBedrag) : this.formatBedrag(bmSimulatie.premiegevolgenTotaal);
      this.tekstenBmGevolgen.push('Als de reparatiekosten lager zijn dan ' + bedrag
        + ' kun je de schade misschien beter zelf betalen. Je moet dat bedrag dan wel in 1 keer kunnen én willen betalen aan de reparateur.');
    } else {
      // fallback tekst als de bm-simulatie, waar bmtrede weer op het oude niveau is, niet bepaald kan worden
    }
  }

  geefBmTekstBedragBekend() {
    let bmSimulatie = this.geefJuisteBMSimulatie();
    let extraPremie = bmSimulatie.premiegevolgenTotaal;
    if (this.eigenRisicoBedragAanwezig) {
      extraPremie = extraPremie + this.eigenRisicoBedrag;
    }

    if (extraPremie > this.geschatSchadeBedrag) {
      this.tekstenBmGevolgen.push('Je kunt de reparatie misschien <span class="ols-bold">beter zelf betalen</span>. Je moet dat bedrag dan wel in 1 keer kunnen én willen betalen.');
    } else {
      this.tekstenBmGevolgen.push('Je kunt de reparatie <span class="ols-bold">het beste door ons</span> laten betalen. Dit mag je natuurlijk zelf bepalen.');
    }

    let tekst = 'In ' + bmSimulatie.berekendJaar + ' jaar betaal je ongeveer ' + this.formatBedrag(bmSimulatie.premiegevolgenTotaal) + ' extra premie.';
    if (this.eigenRisicoBedragAanwezig) {
      tekst += ' Ook betaal je ' + this.eigenRisicoBedragFormat + ' eigen risico. Dit is totaal <span class="ols-bold">' +
        this.formatBedrag(extraPremie) + '</span>.';
    }
    this.tekstenBmGevolgen.push(tekst);

    tekst = 'Je reparatie kost ' + FormatHelper.formatCurrency(this.geschatSchadeBedrag, 'EUR') + '</span>. Dit is ';
    if (extraPremie > this.geschatSchadeBedrag) {
      tekst += 'minder';
    } else {
      tekst += 'meer';
    }
    tekst += ' dan de kosten die je aan Interpolis betaalt.';
    this.tekstenBmGevolgen.push(tekst);
  }

  bepaalGegevensLijsten() {
    this.bmSimulaties = [];
    this.eigenRisicoBedragAanwezig = false;
    this.geschatSchadeBedrag = 0;

    const vragenboomresponse = this._dataService.getVragenboomResponse();
    if (vragenboomresponse.BMSimulaties?.length > 0) {
      this.bmSimulaties = vragenboomresponse.BMSimulaties;
    }
    if (vragenboomresponse.beschadigdeVoertuigen?.length > 0) {
      this.geschatSchadeBedrag = vragenboomresponse.beschadigdeVoertuigen[0].geschatSchadeBedrag;
    }
    // workaround voor Eigen Risico, als er niets terug komt dan nul en er kunnen meerdere limietenEnEigenRisicos terug komen. Dan is het hoogste bedrag leidend
    this.eigenRisicoBedrag = 0;
    if (vragenboomresponse.limietenEnEigenRisicos?.length > 0) {
      vragenboomresponse.limietenEnEigenRisicos.forEach((limER) => {
        if (limER.eigenRisicoNum && limER.eigenRisicoNum > this.eigenRisicoBedrag) {
          this.eigenRisicoBedrag = limER.eigenRisicoNum;
          this.eigenRisicoBedragAanwezig = true;
        }
      });
    }
    this.eigenRisicoBedragFormat = this.formatBedrag(this.eigenRisicoBedrag);
  }

  bepaalTekstkey(): string {
    let tekstKey = 'bmbelangrijk2025_';

    if (this.bmSimulaties[0].krtpercNietClaimen < 0 && this.bmSimulaties[0].krtpercNietClaimen === this.bmSimulaties[0].krtpercWelClaimen) {
      tekstKey += 'toeslag';
    } else {
      if (this.bmSimulaties[0].noClaimBeschermerNuIngezet) {
        // NCG voor deze schade
        if (this.bmSimulaties[0].krtpercNietClaimen === this.bmSimulaties[0].krtpercWelClaimen) {
          if (this.bmSimulaties[0].bmtredeNietClaimen === this.bmSimulaties[0].bmtredeWelClaimen) {
            tekstKey += 'welNCG_krtEnBmtredeGelijk';
          } else {
            tekstKey += 'welNCG_bmtredeOmlaag';
          }
        } else {
          tekstKey += 'welNCG_krtpercOmlaag';
        }
      } else {
        // geen NCG
        if (this.bmSimulaties[0].krtpercNietClaimen === this.bmSimulaties[0].krtpercWelClaimen
          && this.bmSimulaties[0].bmtredeNietClaimen === this.bmSimulaties[0].bmtredeWelClaimen) {
          tekstKey += 'geenNCG_geen_bmgevolgen';
        } else {
          tekstKey += 'geenNCG';
        }
      }
    }

    return tekstKey;
  }

  bepaalBonusMalusTeksten(): void {
    if (this.bmSimulaties?.length > 0) {
      this._sharedDataService.getTekst(this.bepaalTekstkey()).pipe(takeUntil(this.unsubscribe$)).subscribe(tekst => {
        if (tekst?.length > 0) {
          this.bepaalTekstenBmGrijs(tekst);
        }
        this.bepaalTekstenBmgevolgen();
      });
    }
  }

  tekstReplaceBM(tekst: string): string[] {
    if (tekst?.length > 0) {
      if (tekst.indexOf("<aantalSchade>") > 0) {
        tekst = tekst.replace('<aantalSchade>', this.bmSimulaties[0].aantalClaimsTotaal?.toString())
      }
      if (tekst.indexOf("<tredeVan>") > 0) {
        tekst = tekst.replace('<tredeVan>', (this.bmSimulaties[0].bmtredeNietClaimen - 1).toString());
      }
      if (tekst.indexOf("<tredeNaar>") > 0) {
        tekst = tekst.replace('<tredeNaar>', this.bmSimulaties[0].bmtredeWelClaimen ? this.bmSimulaties[0].bmtredeWelClaimen.toString() : '');
      }
      if (tekst.indexOf("<aantalJaar>") > 0) {
        let bmSimulatie = this.geefJuisteBMSimulatie();
        if (bmSimulatie?.berekendJaar > 0) {
          tekst = tekst.replace('<aantalJaar>', bmSimulatie.berekendJaar.toString());
        } else {
          // aantal jaar kan niet bepaald worden, dus tekstregel weg
          return tekst.split('|').filter(t => t.indexOf("<aantalJaar>") < 0);
        }
      }
    }
    return tekst.split('|');
  }

  bepaalTekstenBmgevolgen() {
    if (this.bmSimulaties?.length > 0 && this.bmSimulaties[0].krtpercNietClaimen === this.bmSimulaties[0].krtpercWelClaimen) {
      if (this.bmSimulaties[0].krtpercNietClaimen < 0) {
        this.tekstenBmGevolgen.push(this.geefBmTekstPremietoeslag());
      } else {
        this.tekstenBmGevolgen.push(this.geefBmTekstGeenMeerpremie());
      }
    }
    else if (this.geschatSchadeBedrag > 0) {
      this.geefBmTekstBedragBekend();
    } else {
      this.geefBmTekstBedragOnbekend();
    }

    if (this.bmSimulaties[0].premiegevolgenPerJaar && this.bmSimulaties[0].premiegevolgenPerJaar > 0) {
      this.bmgevolgenDisclaimerTonen = true;
    }
  }

  bepaalTekstenBmGrijs(tekst: string) {
    if (tekst?.length > 0) {
      this.tekstenBmBelangrijk = this.tekstReplaceBM(tekst);

      if (this.eigenRisicoBedragAanwezig) {
        if (this.eigenRisicoBedrag === 0) {
          this.tekstenBmBelangrijk.push("Je hebt geen eigen risico.");
        } else {
          this.tekstenBmBelangrijk.push("Je hebt een eigen risico van " + this.eigenRisicoBedragFormat + ".");
        }
      }
    }
  }

  geefRegelAntwoord(antw: Antwoord, regel: string, metEuroteken: boolean): string {
    return this.geefRegel(regel, metEuroteken, antw.formaat);
  }

  geefRegel(regel: string, metEuroteken: boolean, formaat: string): string {
    if (regel.indexOf('{') < 0) {
      return regel;
    }
    return VraagHelper.checkVariabeleWaarden(regel, metEuroteken, formaat as Formaat, this._dataService.getVragenboomResponse());
  }

  formatBedrag(bedrag: number): string {
    return '<span class="inline">' + FormatHelper.formatCurrency(bedrag, 'EUR') + '</span>';
  }

  geefIndexPlus(index: number): string {
    return (index + 1).toString();
  }

  geefMeetidButton(titel: string): string {
    return 'ipoclick-btn-' + this._dataService.getMeetidPazaProces() + '-' + MetenMapper.format(titel);
  }

  geefFormaat(antwoord: Antwoord, str: string): Formaat {
    if (antwoord?.formaat?.length > 0) {
      return antwoord.formaat as Formaat;
    } else {
      if (!str) { return null; }

      let index = str.indexOf('{');
      let indexSluit = str.indexOf('}');

      if (index > -1 && indexSluit > -1) {
        let variabele = str.substring(index + 1, indexSluit);
        let indexDubbelePunt = variabele.indexOf(':');
        if (indexDubbelePunt > 0) {
          return variabele.substring(indexDubbelePunt + 1) as Formaat;
        }
      }
    }
    // geen formaat aanwezig
    return null;
  }

  isTotaalbedrag(antwoord: Antwoord): boolean {
    if (!antwoord) {
      return false;
    }
    return this.geefFormaat(antwoord, antwoord.antwoordregels?.length > 0 ? antwoord.antwoordregels[0] : null) === Formaat.BedragTotaal;
  }

  minTeken(antwoord: Antwoord, str: string): string {
    if (this.geefFormaat(antwoord, str) === Formaat.Percentage) {
      return "-";
    }
    return this.geefFormaat(antwoord, str) === Formaat.BedragNegatief ? "-" : "";
  }

  getVraagClass(vraag: Vraag): string[] {
    var classes: string[] = [];
    vraag.styles?.forEach(style => classes.push(`ols-${style}`));
    return classes;
  }

  validate(vraag: Vraag, antwoorden: Antwoord[], resultaat: VraagboomEmptyResultaat): boolean {
    return true;
  }
}
